import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { AuthModal } from '@/components/auth/AuthModal';
import { useAuth } from '@/lib/auth/store';
import { createBookingAndRedirectToStripe } from '@/lib/stripe';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Calendar, MapPin, Users, Clock, ArrowDown, Play, Pause, Volume2, VolumeX, Utensils, Music, Instagram, Wine, ExternalLink } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { MetaTags } from '@/components/MetaTags';

export function AyuniExperience() {
  const { user } = useAuth();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [freezeFrameShowing, setFreezeFrameShowing] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(false);
  
  // Mark page as loaded
  useEffect(() => {
    setPageLoaded(true);
  }, []);
  
  // Initialize Google Maps
  useEffect(() => {
    const mapElement = document.getElementById('venue-map');
    if (!mapElement || !window.google?.maps) return;

    // Using same map configuration as Lucido page
    const location = { lat: 25.8472, lng: -80.1857 };
    const map = new google.maps.Map(mapElement, {
      center: location,
      zoom: 14,
      mapTypeId: "roadmap",
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
        style: google.maps.ZoomControlStyle.SMALL
      }
    });

    // Add a marker at Miami Ironside
    new google.maps.Marker({
      position: location,
      map,
      title: "Miami Ironside - 7580 NE 4th Ct"
    });
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !pageLoaded) return;

    // Preload video data
    video.preload = "auto";
    
    // Handle video loading states
    const handleLoadedMetadata = () => {
      setIsVideoLoaded(true);
      
      // Start the video 1.5 seconds after the page loads
      setTimeout(() => {
        setFreezeFrameShowing(false);
        video.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch(err => {
            console.warn('Autoplay failed:', err);
            setIsPlaying(false);
          });
      }, 1500);
    };

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!freezeFrameShowing) { // Only auto-play if freeze frame is not showing
          video.play().catch(err => console.warn('Visibility play failed:', err));
          setIsPlaying(true);
        }
      } else {
        video.pause();
        setIsPlaying(false);
      }
    };

    // Add event listeners
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('pause', () => setIsPlaying(false));

    // Start loading the video
    video.load();

    // Cleanup
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('play', () => setIsPlaying(true));
      video.removeEventListener('pause', () => setIsPlaying(false));
    };
  }, [pageLoaded]);

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    
    const video = videoRef.current;
    if (!video) return;

    // If we're showing the freeze frame, cancel it and play immediately
    if (freezeFrameShowing) {
      setFreezeFrameShowing(false);
    }

    if (video.paused) {
      video.play()
        .then(() => setIsPlaying(true))
        .catch(error => {
          console.error('Play failed:', error);
          setIsPlaying(false);
        });
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handlePurchaseClick = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    try {
      // Use the generalized function to create a booking and redirect to Stripe
      const experienceId = "b3047107-517f-4b40-b3e0-4dc4ca781e6f";
      const checkoutUrl = "https://buy.stripe.com/bIY3e61aVgKnboQ7tt";
      const participantCount = 1; // Default to 1 participant per booking
      const totalAmount = 150; // Price shown in the UI
      
      await createBookingAndRedirectToStripe(
        experienceId,
        checkoutUrl,
        participantCount,
        totalAmount
      );
    } catch (error) {
      console.error('Failed to create booking:', error);
    }
  };

  const scrollToContent = () => {
    const contentSection = document.getElementById('content-section');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="relative min-h-screen bg-earth-900">
      <MetaTags 
        title="Ayuni | Interactive Mediterranean Dining | rendezvous"
        description="An interactive and intimate dining experience celebrating multicultural Mediterranean cuisine through fresh ingredients, music, and community."
        image="https://rendezvouswebsite.b-cdn.net/Ayuni/WhatsApp%20Image%202025-04-17%20at%2017.55.45_a7e318e9.jpg"
        url="https://letsrendezvous.co/experiences/ayuni"
      />

      {/* Hero Section with Video */}
      <div className="relative w-full h-[100vh] overflow-hidden pt-10">
        {/* Hero Video Container with Center Alignment */}
        <div className="absolute inset-0 top-20 flex justify-center items-center bg-earth-900 overflow-hidden">
          {/* Poster Image/Freeze Frame */}
          <div 
            className={`absolute inset-0 z-10 transition-opacity duration-500 ${freezeFrameShowing ? 'opacity-100' : 'opacity-0'}`}
          >
            <img
              src="https://rendezvouswebsite.b-cdn.net/Ayuni/WhatsApp%20Image%202025-04-17%20at%2017.55.45_a7e318e9.jpg"
              alt="Ayuni Dining Experience"
              className="w-full h-full object-cover object-center"
            />
          </div>
          
          {/* Video Element */}
          <video
            ref={videoRef}
            src="https://rendezvouswebsite.b-cdn.net/Ayuni/Copy%20of%20wide_v2.mp4"
            className={`w-full h-full object-cover transition-opacity duration-1000 ${freezeFrameShowing ? 'opacity-0' : 'opacity-100'}`}
            muted={isMuted}
            loop
            playsInline
            poster="https://rendezvouswebsite.b-cdn.net/Ayuni/WhatsApp%20Image%202025-04-17%20at%2017.55.45_a7e318e9.jpg"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-earth-900/50 to-earth-900" />

          {/* Video Controls */}
          <div className="absolute bottom-10 right-10 flex items-center gap-4 z-20">
            <button
              onClick={handlePlayPause}
              className="p-3 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800 transition-colors cursor-pointer"
            >
              {isPlaying && !freezeFrameShowing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
            </button>
            <button
              onClick={handleToggleMute}
              className="p-3 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800 transition-colors cursor-pointer"
            >
              {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="absolute inset-0 top-20 bottom-0 flex flex-col items-center justify-end px-4 text-center pb-36">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display mb-6">AYUNI</h1>
            <p className="text-xl md:text-2xl text-sand-200 mb-8">A communal Mediterranean dining experience</p>
          </motion.div>

          {/* Scroll Down Indicator */}
          <motion.div
            className="absolute bottom-12 left-0 right-0 flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <button 
              onClick={scrollToContent}
              className="flex flex-col items-center text-sand-300 hover:text-sand-100 transition-colors"
            >
              <span className="text-sm uppercase tracking-widest mb-2">Discover</span>
              <ArrowDown className="h-6 w-6 animate-bounce" />
            </button>
          </motion.div>
        </div>
      </div>
      
      {/* Reserve Button Section - Now outside the hero */}
      <div className="relative bg-earth-900 py-12 text-center">
        <Button 
          size="lg"
          onClick={handlePurchaseClick}
          className="text-lg px-8 py-4"
        >
          Reserve Your Spot
        </Button>
      </div>

      {/* Content Section */}
      <div id="content-section" className="container mx-auto px-4 py-24">
        <div className="max-w-4xl mx-auto">
          {/* Sidebar Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="about" className="space-y-8">
                <TabsList className="w-full">
                  <TabsTrigger value="about">About</TabsTrigger>
                  <TabsTrigger value="experience">The Experience</TabsTrigger>
                  <TabsTrigger value="gallery">Gallery</TabsTrigger>
                </TabsList>
                
                <TabsContent value="about" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">About Ayuni</h2>
                  <div className="space-y-4 text-sand-300">
                    <p>
                      Ayuni is an interactive and intimate dining experience that celebrates multicultural Mediterranean cuisine through fresh ingredients, music, and an upbeat feast. Nestled between the flavors of the Mediterranean and the Middle East, we craft events that tell a story—inviting guests to immerse themselves in a rich tapestry of culture, flavor, and joy.
                    </p>
                    <p>
                      We believe in the beauty of shared moments, where every dish becomes part of a larger narrative, and every gathering is a celebration of life's simple pleasures.
                    </p>
                    
                    <div className="pt-4">
                      <h3 className="text-xl font-display mb-3">Our Philosophy</h3>
                      <p>
                        At Ayuni, we're committed to creating meaningful connections through food. We believe dining should be more than just eating—it should be immersive, interactive, and filled with moments of discovery and delight. Every ingredient is thoughtfully selected, every plate carefully presented, and every gathering uniquely curated.
                      </p>
                    </div>

                    <div className="bg-earth-800/50 p-6 rounded-lg mt-6">
                      <h3 className="text-xl font-display mb-3">Our Partners</h3>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sand-300 mb-2">
                            Wine provided by our friends at <a href="https://www.antigalwinery.com/" target="_blank" rel="noopener noreferrer" className="text-sand-100 hover:text-sand-200 underline">Antigal Winery</a>.
                          </p>
                        </div>
                        <div>
                          <p className="text-sand-300 mb-2">
                            Special thanks to <a href="https://www.drinkbrez.com/" target="_blank" rel="noopener noreferrer" className="text-sand-100 hover:text-sand-200 underline">Brez</a> for providing cannabis cocktails.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <img 
                    src="https://rendezvouswebsite.b-cdn.net/Ayuni/WhatsApp%20Image%202025-04-17%20at%2017.55.45_a7e318e9.jpg" 
                    alt="Ayuni Dining Experience" 
                    className="w-full h-auto rounded-lg mt-6"
                  />
                  
                  {/* Location information moved from Location tab */}
                  <div className="mt-10 pt-6 border-t border-earth-700">
                    <h3 className="text-2xl font-display mb-6">Location</h3>
                    
                    <div className="flex items-center justify-center gap-2 text-sand-300 mb-4">
                      <MapPin className="h-5 w-5" />
                      <span>Miami Ironside - 7580 NE 4th Ct, Miami, FL 33138</span>
                    </div>
                    
                    <div 
                      id="venue-map"
                      className="w-full h-[300px] rounded-lg overflow-hidden"
                    />
                    
                    <div className="mt-6 flex justify-center">
                      <a
                        href="https://maps.google.com/?q=Miami+Ironside+7580+NE+4th+Ct+Miami+FL+33138"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sand-100 hover:text-sand-300 underline transition-colors"
                      >
                        <MapPin className="h-4 w-4" />
                        <span>Open in Google Maps</span>
                      </a>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="experience" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">The Ayuni Experience</h2>
                  <div className="space-y-4 text-sand-300">
                    <p>
                      With Ayuni, the concept revolves around a unique, communal dining experience without traditional seating. This arrangement fosters genuine connections as guests naturally interact and engage with each other. Inspired by the vibrant culture of Israel, our tables are transformed into lively canvases filled with the colors and flavors of Mediterranean cuisine.
                    </p>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg mt-6">
                      <h3 className="text-xl font-display mb-4">What to Expect</h3>
                      <ul className="list-disc list-inside space-y-3">
                        <li>Standing communal dining that encourages genuine interactions</li>
                        <li>Constantly evolving array of small plates brought to your table</li>
                        <li>Mediterranean and Middle Eastern flavors featuring the freshest ingredients</li>
                        <li>Curated music that complements the dining atmosphere</li>
                        <li>Wine from our friends at <a href="https://www.antigalwinery.com/" target="_blank" rel="noopener noreferrer" className="text-sand-100 hover:text-sand-200 underline">Antigal Winery</a></li>
                        <li>Cannabis cocktails by <a href="https://www.drinkbrez.com/" target="_blank" rel="noopener noreferrer" className="text-sand-100 hover:text-sand-200 underline">Brez</a></li>
                        <li>A dining experience that engages all your senses</li>
                      </ul>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <h4 className="text-lg font-medium text-sand-100 mb-3">The Menu</h4>
                        <p>
                          Our menu changes with the seasons and inspiration, but always centers around Mediterranean staples: fresh breads, vibrant dips, roasted vegetables, grilled meats and seafood, and herb-infused delicacies. Each dish is designed to be shared, creating a mosaic of flavors throughout your experience.
                        </p>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <h4 className="text-lg font-medium text-sand-100 mb-3">The Atmosphere</h4>
                        <p>
                          Expect a lively, convivial atmosphere with music that evolves throughout the evening. The energy builds naturally as the night progresses, creating an organic flow from relaxed dining to festive celebration, all while maintaining the intimate connection to the food and each other.
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="gallery" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Gallery</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Ayuni/WhatsApp%20Image%202025-04-17%20at%2017.55.45_a7e318e9.jpg" 
                      alt="Ayuni Feast Table" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Ayuni/IMG-20250417-WA0011.jpg" 
                      alt="Mediterranean Dining" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Ayuni/IMG-20250417-WA0010.jpg" 
                      alt="Mediterranean Spread" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Ayuni/IMG-20250417-WA0012.jpg" 
                      alt="Shared Plates" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Ayuni/IMG-20250417-WA0013.jpg" 
                      alt="Interactive Dining" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="sticky top-24 space-y-6">
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-display">Price</h3>
                    <div className="text-2xl font-display">$150 (incl. tax)</div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sand-300">
                      <Clock className="h-5 w-5 text-sand-400" />
                      <span>6-9PM</span>
                    </div>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-5 w-5 text-sand-400" />
                      <span>April 27th</span>
                    </div>
                    <Button 
                      size="lg"
                      onClick={handlePurchaseClick}
                      className="w-full text-md px-6 py-4"
                    >
                      Reserve Your Spot
                   </Button>
                  </div>
                </div>

                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">Host</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src="https://rendezvouswebsite.b-cdn.net/Ayuni/IMG-20250417-WA0013.jpg" 
                      alt="Chef Ayuni"
                      className="w-16 h-16 rounded-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "https://api.dicebear.com/7.x/initials/svg?seed=AYUNI&backgroundColor=1d1918&textColor=e8e4dc";
                      }}
                    />
                    <div>
                      <p className="text-sand-100 font-medium">Ayuni</p>
                      <p className="text-sand-400 text-sm">Culinary Experience Creator</p>
                    </div>
                  </div>
                  <p className="text-sand-300 text-sm mb-4">
                    Ayuni crafts immersive Mediterranean dining experiences that blend traditional flavors with contemporary presentation. Each gathering is designed to create meaningful connections through food, culture, and shared moments.
                  </p>
                  <a 
                    href="https://www.instagram.com/ayunichef/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sand-300 hover:text-sand-200 transition-colors"
                  >
                    <Instagram className="h-4 w-4" />
                    <span className="underline">@ayunichef</span>
                  </a>
                </div>
                
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">What's Included</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Utensils className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Multi-course Mediterranean feast</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Music className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Curated music selection</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Users className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Intimate, interactive dining setting</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Wine className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">
                        Wine from <a href="https://www.antigalwinery.com/" target="_blank" rel="noopener noreferrer" className="text-sand-100 hover:text-sand-200 underline">Antigal Winery</a>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <ExternalLink className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">
                        Cannabis cocktails by <a href="https://www.drinkbrez.com/" target="_blank" rel="noopener noreferrer" className="text-sand-100 hover:text-sand-200 underline">Brez</a>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="mt-24 bg-earth-800/50 p-8 rounded-lg text-center">
            <h2 className="text-3xl font-display mb-6">Join the Table</h2>
            <p className="text-sand-300 mb-8 max-w-2xl mx-auto">
              Experience the joy of shared Mediterranean cuisine in a unique, standing communal setting. Spaces are limited to ensure an intimate and special dining experience.
            </p>
            <Button 
              size="lg"
              onClick={handlePurchaseClick}
              className="text-lg px-8 py-4"
            >
              Reserve Your Spot
            </Button>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab="sign-up"
        redirectTo={location.pathname}
      />
    </div>
  );
}

export default AyuniExperience;